import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { TypedMemo } from '../../../common/types';
import { useChartTokens } from '../../theme/useChartTokens';
import { StreamingChartData } from '../buildStreamingChartData';
import { sourceLook } from '../streamingSources';
import { countryName, formatDay } from '../streamingFormat';
import { streamingBlockMessages as messages } from '../streamingBlockMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StreamingLineChartProps {
  data: StreamingChartData;
  /** The label of the one line of the `total` split. */
  totalLabel: string;
  /** True: a dimension line is a country code and shows the country name. */
  dimensionIsCountry?: boolean;
  height?: number;
}

/** The lines of the Streaming chart in the chart ink of the theme (design b1). */
const StreamingLineChartComponent: React.FC<StreamingLineChartProps> = ({
  data,
  totalLabel,
  dimensionIsCountry = false,
  height = 260,
}) => {
  const intl = useIntl();
  const chart = useChartTokens();
  const palette = useMemo(
    () => [chart.brand, chart.pos, chart.brandLift, chart.neg, chart.flat, chart.axis],
    [chart.axis, chart.brand, chart.brandLift, chart.flat, chart.neg, chart.pos]
  );

  const lines = useMemo(
    () =>
      data.lines.map((line, index) => {
        if (line.kind === 'source') {
          const look = sourceLook(line.label);
          return { ...line, name: look.name, color: chart[look.chartKey] };
        }
        if (line.kind === 'total') {
          return { ...line, name: totalLabel, color: chart.brand };
        }
        const name = dimensionIsCountry ? countryName(intl, line.label) : line.label;
        return { ...line, name, color: palette[index % palette.length] };
      }),
    [chart, data.lines, dimensionIsCountry, intl, palette, totalLabel]
  );

  if (data.rows.length === 0) {
    return <p className={styles.faint}>{intl.formatMessage(messages.chartEmpty)}</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data.rows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chart.grid} />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 11, fill: chart.axis }}
          tickFormatter={(day: string) => formatDay(intl, day)}
        />
        <YAxis tick={{ fontSize: 11, fill: chart.axis }} allowDecimals={false} width={56} />
        <Tooltip contentStyle={{ backgroundColor: chart.tooltipBg, color: chart.tooltipTx }} />
        <Legend wrapperStyle={{ fontSize: 12, color: chart.label }} />
        {lines.map((line) => (
          <Line
            key={line.key}
            type="monotone"
            dataKey={line.key}
            name={line.name}
            stroke={line.color}
            strokeWidth={line.kind === 'total' ? 2 : 1.5}
            dot={false}
            connectNulls={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

export const StreamingLineChart = TypedMemo(StreamingLineChartComponent);
